import { Button, Icon, Input, Modal, Popup, Select, Table, VerticalMenu } from '@scuf/common';
import useFetch from 'helpers/hooks/useFetch';
import { EmailServiceType, ValidateCompanyEmailFields } from 'models/repaircenter/detailsRma';
import { useTranslation } from 'react-i18next';
import { COMPANY_MANAGER, USER_MANAGER, cnpjMask, telephoneMask } from 'resources';
import Alert from './alert';
import { useState } from 'react';
import { CompanyDTO, latamCountries } from 'models/userManager/addCustomer';
import { UserPortal } from 'models';
import { downloadPublicFile } from 'resources/api/general_api/PublicFiles';
import i18n from 'i18n';

type ModalProps = {
    modalState: 'pending' | 'email' | 'company' | 'success' | undefined;
    onChangeModalState: (state: string | undefined) => void;
    user: UserPortal;
};

function shouldShowFields(modalState: ModalProps['modalState']) {
    return modalState !== 'success';
}

export const CompanySelectionModal: React.FC<ModalProps> = ({ modalState, onChangeModalState, user }: ModalProps) => {
    const { t } = useTranslation(['home', 'homets', 'common']);
    const [popupOpen, setPopupOpen] = useState(false);
    const [country, setCountry] = useState<String>();
    const [company, setCompany] = useState<CompanyDTO>();

    const [, sendEmail, sendingEmail] = useFetch<EmailServiceType<ValidateCompanyEmailFields>>({
        url: COMPANY_MANAGER.sendEmail,
        method: 'POST',
        onError: 'Send',
        onSuccess: 'Send'
    });

    const [, userUpdate, updatingUser] = useFetch<{ ssoId: string; companyId: number }>({
        url: USER_MANAGER.updateUserCompany,
        method: 'POST',
        onError: 'Change',
        onSuccess: 'Change'
    });

    const [fields, setFields] = useState<ValidateCompanyEmailFields>({ companyName: '', email: '', cnpj: '', idSap: '' });

    const [, getCompanies, findingCompany] = useFetch<{ companyDto: string }, CompanyDTO>({
        url: COMPANY_MANAGER.getCompanyByCnpjLogin,
        onError: 'LoadInfo'
    });

    const [, getCompaniesByIdSap, findingCompanyByIdSap] = useFetch<{ idSap: string }, CompanyDTO>({
        url: COMPANY_MANAGER.GetCompanyByIdSap,
        onError: 'LoadInfo'
    });

    const handleClick = () => {
        if (modalState === 'company') onChangeModalState('email');
        else {
            onChangeModalState('company');
            setFields({ cnpj: fields.cnpj, companyName: '', email: '', idSap: '' });
        }
    };

    const validateCNPJ = (): boolean => {
        return (fields.cnpj?.match(/\d+/g)?.join('')!.length ?? 0) >= 14;
    };

    const validateSendEmail = (): boolean => {
        return (
            ((fields.cnpj?.match(/\d+/g)?.join('')!.length ?? 0) >= 14 || fields.idSap !== null) &&
            !!fields?.companyName?.length! &&
            !!fields.email.length
        );
    };

    async function UpdateUser() {
        await userUpdate({ ssoId: user.ssoId, companyId: company?.id! }, _ => onChangeModalState('pending'));
    }

    return (
        <Modal className="modalW600" size="small" closeOnDocumentClick={false} open={!!modalState} closeOnDimmerClick={false}>
            <div className="modalConsultWaranty">
                <Modal.Header>
                    <div className="modalHeader" style={{ position: 'relative' }}>
                        <h2 style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                            {modalState === 'pending'
                                ? t('consultCompany.pendingApproval')
                                : modalState === 'success'
                                  ? 'Cadastro Digital - Informações Importantes'
                                  : t('consultCompany.selectCompany')}
                        </h2>
                        <Popup
                            open={popupOpen}
                            position="bottom right"
                            onClose={() => setPopupOpen(false)}
                            on="click"
                            element={
                                <Button
                                    type="primary"
                                    icon={<Icon name="globe" root="common" />}
                                    onClick={() => setPopupOpen(prev => !prev)}
                                >
                                    {' '}
                                </Button>
                            }
                        >
                            <VerticalMenu>
                                <VerticalMenu.Item
                                    onClick={() => {
                                        i18n.changeLanguage('pt');
                                        setPopupOpen(false);
                                    }}
                                >
                                    Português
                                </VerticalMenu.Item>
                                <VerticalMenu.Item
                                    onClick={() => {
                                        i18n.changeLanguage('en');
                                        setPopupOpen(false);
                                    }}
                                >
                                    English
                                </VerticalMenu.Item>
                                <VerticalMenu.Item
                                    onClick={() => {
                                        i18n.changeLanguage('es');
                                        setPopupOpen(false);
                                    }}
                                >
                                    Español
                                </VerticalMenu.Item>
                            </VerticalMenu>
                        </Popup>
                    </div>
                </Modal.Header>
                <Modal.Content>
                    {modalState !== 'success' && (
                        <Alert
                            text={
                                modalState === 'pending' ? t('consultCompany.infoPendency') : t('consultCompany.infoNewCompany')
                            }
                            className="message-initial"
                        />
                    )}
                    {modalState === 'pending' ? (
                        <div style={{ marginTop: '25px', marginBottom: '20px', textAlign: 'justify' }}>
                            {t('consultCompany.infoPendency_2')}
                        </div>
                    ) : null}
                    {modalState === 'pending' ? (
                        <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <strong>
                                    {t('consultCompany.infoPendencyBrasil')}
                                    <a href="mailto:centrodereparos@honeywell.com">centrodereparos@honeywell.com</a>
                                </strong>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <strong>
                                    {t('consultCompany.infoPendencyLatAm')}
                                    <a href="mailto:hsmserviciocsar@honeywell.com">hsmserviciocsar@honeywell.com</a>
                                </strong>
                            </div>
                        </div>
                    ) : null}
                    {shouldShowFields(modalState) && (
                        <>
                            {modalState !== 'pending' && (
                                <Select
                                    label={t('consultCompany.country')}
                                    options={latamCountries}
                                    value={country}
                                    search={true}
                                    onChange={value => setCountry(value)}
                                />
                            )}
                            {modalState !== 'pending' && country === 'Brasil' && (
                                <Input
                                    fluid
                                    label={t('consultCompany.id')}
                                    value={cnpjMask(fields.cnpj ?? '')}
                                    onChange={cnpj => {
                                        if ((cnpj.match(/\d+/g)?.join('')!.length ?? 0) <= 14)
                                            setFields(old => ({ ...old, cnpj }));
                                    }}
                                />
                            )}
                            {modalState !== 'pending' && country !== 'Brasil' && country != null && (
                                <Input
                                    fluid
                                    label={'IdSap'}
                                    value={fields.idSap ?? ''}
                                    onChange={idSap => setFields(old => ({ ...old, idSap }))}
                                />
                            )}
                        </>
                    )}

                    {
                        {
                            company: (
                                <>
                                    <div className="buttonsSearch" style={{ justifyContent: 'space-between' }}>
                                        <a href="#" onClick={handleClick}>
                                            {t('consultCompany.notCompany')}
                                        </a>
                                        <Button
                                            content={t('common:actions.Search')}
                                            size="small"
                                            loading={findingCompany || findingCompanyByIdSap}
                                            disabled={(!validateCNPJ() && !country) || findingCompany || findingCompanyByIdSap}
                                            onClick={() => {
                                                country === 'Brasil'
                                                    ? getCompanies(
                                                          {
                                                              companyDto: fields.cnpj!.match(/\d+/g)?.join('')!
                                                          },
                                                          current => {
                                                              setCompany(current.data);
                                                          }
                                                      )
                                                    : getCompaniesByIdSap(
                                                          {
                                                              idSap: fields.idSap!
                                                          },
                                                          current => {
                                                              setCompany(current.data);
                                                          }
                                                      );
                                            }}
                                        />
                                    </div>
                                    {!!company && (
                                        <>
                                            <Table>
                                                <Table.Header>
                                                    <Table.HeaderCell content={t('CNPJ')} />
                                                    <Table.HeaderCell content={t('user:address.Company')} />
                                                    <Table.HeaderCell content={t('common:translation.Modules')} />
                                                </Table.Header>
                                                <Table.Body>
                                                    <Table.Row>
                                                        <Table.Cell>{company?.cnpj} </Table.Cell>
                                                        <Table.Cell>{company?.name} </Table.Cell>
                                                        <Table.Cell>
                                                            <div
                                                                style={{
                                                                    overflowY: 'scroll',
                                                                    overflowX: 'unset',
                                                                    maxHeight: '150px'
                                                                }}
                                                            >
                                                                {company?.companyLOB?.map(e => (
                                                                    <p key={e.lob.lobCode}>{e.lob.lobCode}</p>
                                                                ))}
                                                            </div>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                </Table.Body>
                                            </Table>
                                            <div className="buttonsSearch">
                                                <Button
                                                    content={t('common:actions.Register')}
                                                    size="small"
                                                    loading={updatingUser}
                                                    onClick={() => UpdateUser()}
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            ),
                            email: (
                                <>
                                    <Input
                                        fluid
                                        label="E-mail"
                                        value={fields.email}
                                        onChange={email => setFields(old => ({ ...old, email }))}
                                    />
                                    <Input
                                        fluid
                                        label={t('consultCompany.companyName')}
                                        value={fields.companyName}
                                        onChange={companyName => setFields(old => ({ ...old, companyName }))}
                                    />
                                    <Input
                                        fluid
                                        label={t('consultCompany.phone')}
                                        value={telephoneMask(fields.phone ?? '')}
                                        onChange={phone => setFields(old => ({ ...old, phone }))}
                                    />
                                    <div className="buttonsSearch" style={{ justifyContent: 'space-between' }}>
                                        <a href="#" onClick={handleClick}>
                                            {t('common:actions.Back')}
                                        </a>
                                        <Button
                                            content={t('common:actions.Send')}
                                            size="small"
                                            loading={sendingEmail}
                                            disabled={!validateSendEmail() || sendingEmail}
                                            onClick={() => {
                                                sendEmail(
                                                    {
                                                        NewCompany: true,
                                                        Body: 'Criação de Companhia',
                                                        Subject: 'Criação de Companhia',
                                                        ToEmail:
                                                            country === 'Brasil'
                                                                ? process.env.REACT_APP_REPAIR_CENTER_EMAIL!
                                                                : process.env.REACT_APP_HSL_EMAIL!,
                                                        Fields: fields
                                                    },
                                                    () => onChangeModalState('success')
                                                );
                                            }}
                                        />
                                    </div>
                                </>
                            ),
                            pending: null,
                            success: (
                                <>
                                    <Modal.Content>
                                        <h2>{t('common:info.digitalRegistrationTitle')}</h2>
                                        <p>{t('common:info.digitalRegistrationText1')}</p>
                                        <p>{t('common:info.digitalRegistrationText2')}</p>
                                        <ul>
                                            <li>
                                                <a
                                                    href="https://vhpwas.honeywell.com/dma/index.html"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {t('common:info.digitalRegistrationLinkRegister')}
                                                </a>
                                            </li>
                                            <li>
                                                <a
                                                    href="https://www.youtube.com/watch?v=TgMI81JdleU"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {t('common:info.digitalRegistrationLinkVideo')}
                                                </a>
                                            </li>
                                            <li>
                                                <strong>{t('common:info.digitalRegistrationManualsTitle')}</strong>{' '}
                                                {t('common:info.digitalRegistrationManualsDescription')}
                                                <ul>
                                                    <li>
                                                        <div
                                                            className="no-padding blue-text"
                                                            style={{
                                                                cursor: 'pointer',
                                                                textDecoration: 'underline',
                                                                display: 'inline-block',
                                                                marginRight: '16px'
                                                            }}
                                                            onClick={() =>
                                                                downloadPublicFile(
                                                                    'digital-form-go-to-guide',
                                                                    'Digital-Form-Go-To-Guide.pdf'
                                                                )
                                                            }
                                                        >
                                                            {t('common:info.digitalRegistrationManual1')}
                                                        </div>
                                                    </li>
                                                    <li>
                                                        <div
                                                            className="no-padding blue-text"
                                                            style={{
                                                                cursor: 'pointer',
                                                                textDecoration: 'underline',
                                                                display: 'inline-block',
                                                                marginRight: '16px'
                                                            }}
                                                            onClick={() =>
                                                                downloadPublicFile(
                                                                    'customer-creation-flyer',
                                                                    'Customer-Creation-Flyer.pdf'
                                                                )
                                                            }
                                                        >
                                                            {t('common:info.digitalRegistrationManual2')}
                                                        </div>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                        <p>{t('common:info.digitalRegistrationHelp')}</p>
                                        <p>
                                            {t('common:info.digitalRegistrationEmail')}{' '}
                                            <a href={`mailto:${t('common:info.digitalRegistrationEmailAddress')}`}>
                                                {t('common:info.digitalRegistrationEmailAddress')}
                                            </a>
                                        </p>
                                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                                            <Button
                                                content={t('common:info.close')}
                                                size="small"
                                                onClick={() => onChangeModalState(undefined)}
                                            />
                                        </div>
                                    </Modal.Content>
                                </>
                            )
                        }[modalState!]
                    }
                </Modal.Content>
            </div>
        </Modal>
    );
};
